/**
 * @file BlendDetailsPanel.js
 * @version 1.7.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-08-24
 * @modified 2026-09-08
 * @description Who a blend is for and how it is quoted — the third column of the
 *              calculator.
 *
 *              These fields used to be collected in the export modal, which
 *              meant a saved blend did not know its own customer until somebody
 *              exported it, and the person who built the blend was rarely the
 *              person filling them in. They are entered here instead, up front,
 *              and saved with the blend.
 *
 *              Customer is required. Nothing saves or exports without one, so a
 *              blend always has an owner.
 *
 *              Sits second, between the grid and the analysis. Who the blend is
 *              for is decided before its cost is picked over, and the analysis
 *              panel is what you watch while typing — so it keeps the outside
 *              edge where the eye returns to it.
 */

import React, { useMemo } from 'react';
import Input from './Input';
import Dropdown from './Dropdown';
import Textarea from './Textarea';
import SearchableDropdown from './SearchableDropdown';
// The same sentinel the customer dropdown uses — from a shared module, so the
// two cannot drift onto different strings and nothing imports in a circle.
import { PROSPECT as PROSPECT_VALUE } from '../utils/blendCustomer';
import ComboBox from './ComboBox';
import TickCheckbox from './TickCheckbox';
import useScrollAffordance from '../hooks/useScrollAffordance';
import ScrollMore from './ScrollMore';
import './BlendDetailsPanel.css';

const DELIVERY_TERMS = [
  { value: '', label: 'Not set' },
  { value: 'Delivered', label: 'Delivered' },
  { value: 'Collected', label: 'Collected' },
  { value: 'Ex works', label: 'Ex works' }
];

const PACKAGING = [
  { value: '', label: 'Not set' },
  { value: 'Bulk', label: 'Bulk' },
  { value: 'Tote', label: 'Tote' },
  { value: 'Half tote', label: 'Half tote' },
  { value: 'Bags', label: 'Bags' }
];

function BlendDetailsPanel({
  details, onChange,
  // The reps a blend may be put on, and whether the viewer is one themselves.
  reps = [], isRepUser = false, customers = [],
  customerOptions, customersLoading,
  priceColumns = [], haulageBands = [],
  periodOptions = [],
  missing = [],
  summary = null
}) {
  const set = (field) => (value) => onChange({ ...details, [field]: value });

  /*
   * The prices the calculator has actually worked out, as starting points for
   * the quote: each priced column ex works, and that column delivered on each
   * haulage band.
   *
   * Labelled with where the number came from — "Spot · Ex works", "Summer 26 ·
   * Band B" — because £248 on its own is not a price anyone can check. The
   * VALUE is the bare number, so what gets saved is money and not a sentence.
   *
   * Only columns the blend is actually priced on: a column with unpriced
   * materials would contribute an understated figure, and offering it beside
   * the real ones invites picking it.
   */
  const salePriceOptions = React.useMemo(() => {
    const pricing = summary?.pricing || {};
    const out = [];
    // Every column and every band, exactly the set the cost panel shows —
    // three bases by seven types. Deliberately NOT narrowed to the ticked
    // price columns and NOT de-duplicated on the figure: two bases landing on
    // the same number are two different quotes, and the badges are what tell
    // them apart.
    for (const col of priceColumns) {
      const p = pricing[col.key];
      if (!p) continue;
      const rows = [
        // 'raw_material' is not a haulage band — it is the ex-works figure the
        // bands are added to — so it carries a literal key of its own.
        { typeKey: 'raw_material', type: 'Raw material', amount: p.rawMaterialCost },
        ...haulageBands.map((band) => ({
          typeKey: band.key, type: band.label, amount: p.delivered?.[band.key]
        }))
      ];
      for (const row of rows) {
        const amount = Number(row.amount);
        if (!Number.isFinite(amount)) continue;
        const price = amount.toFixed(2);
        out.push({
          value: price,
          basisKey: col.key,
          typeKey: row.typeKey,
          // A plain number, so the closed field reads as the £/T it is asked
          // for and a stored price still resolves after a reload.
          label: price,
          searchText: `${col.label} ${row.type} ${price} £${price}`,
          renderLabel: (
            <span className="bdp-price-option">
              <span className="bdp-price-tags">
                <span className="bdp-price-badge is-basis">{col.label}</span>
                <span className="bdp-price-badge is-type">{row.type}</span>
              </span>
              <span className="bdp-price-amount">£{price}</span>
            </span>
          )
        });
      }
    }
    return out;
  }, [summary, priceColumns, haulageBands]);
  /*
   * What the stored sale price was based on, resolved back to labels.
   *
   * Keys are stored rather than labels, so a renamed price column reads
   * correctly on an old blend. A key that no longer resolves — a retired
   * period — falls back to showing the key itself, which is still more use
   * than a blank where a basis used to be.
   */
  const salePriceOrigin = React.useMemo(() => {
    const basisKey = details.salePriceBasis;
    const typeKey = details.salePriceType;
    if (!basisKey && !typeKey) return null;
    const basis = priceColumns.find((c) => c.key === basisKey);
    const band = haulageBands.find((b) => b.key === typeKey);
    return {
      basis: basis?.label || basisKey || '—',
      type: typeKey === 'raw_material' ? 'Raw material' : (band?.label || typeKey || '—')
    };
  }, [details.salePriceBasis, details.salePriceType, priceColumns, haulageBands]);

  /*
   * Who the rep field is for, and whether it can be changed.
   *
   * PROSPECT_VALUE is the sentinel the customer dropdown uses for a blend that
   * has no account behind it yet.
   */
  const repOptions = useMemo(
    () => reps.map((r) => ({ value: String(r.id), label: r.name })), [reps]);

  const customerRep = useMemo(() => {
    if (!details.customerId || details.customerId === PROSPECT_VALUE) return null;
    return customers.find((c) => String(c.id) === String(details.customerId)) || null;
  }, [customers, details.customerId]);

  const customerRepName = customerRep?.sales_rep || '';
  const repEditable = details.customerId === PROSPECT_VALUE
    || (!!details.customerId && !customerRep?.rep_id);

  const isMissing = (field) => missing.includes(field);

  // Several price bases can be quoted together — Spot alongside a contract
  // period is a normal thing to show a customer — so this is a tick list rather
  // than a single choice.
  const togglePriceColumn = (key) => {
    const next = new Set(details.priceColumnKeys || []);
    if (next.has(key)) next.delete(key); else next.add(key);
    onChange({ ...details, priceColumnKeys: [...next] });
  };

  // The panel scrolls, and a panel whose content ends level with its frame
  // looks finished. The fade says otherwise while there is more below.
  const [scrollRef, scroll] = useScrollAffordance([details, priceColumns, periodOptions]);

  return (
    <div className="scroll-more-wrap">
    <aside ref={scrollRef} className="blend-details-panel scroll-more-box">
      <div className="bdp-section">
        <h4>Customer</h4>

        <div className={`bdp-field${isMissing('customerId') ? ' bdp-missing' : ''}`}>
          <label htmlFor="bdp-customer">Customer <span className="bdp-required">required</span></label>
          <SearchableDropdown
            value={details.customerId || ''}
            onChange={set('customerId')}
            options={customerOptions}
            placeholder={customersLoading ? 'Loading customers…' : 'Search customers…'}
          />
        </div>

        {/*
          * The rep, shown only to someone who is not one.
          *
          * A rep's own blends are theirs by definition — the server fills it in
          * from users.rep_id — so the field would be a question with one answer
          * and no reason to ask it. For everyone else it says who the blend
          * belongs to.
          *
          * Editable only where the account cannot answer: a New Prospect, or a
          * customer nobody has assigned. Where the account HAS a rep that is the
          * answer and overriding it here would put the blend on one rep while
          * every other screen reads the other off the account.
          */}
        {!isRepUser && (
          <div className="bdp-field">
            <label htmlFor="bdp-rep">Rep</label>
            {repEditable ? (
              <SearchableDropdown
                value={details.repId || ''}
                onChange={set('repId')}
                options={repOptions}
                placeholder={details.customerId ? 'Choose a rep…' : 'Pick a customer first'}
                disabled={!details.customerId}
              />
            ) : (
              <div className={`bdp-readonly${customerRepName ? '' : ' empty'}`}>
                {customerRepName || (details.customerId
                  ? 'No rep on this account'
                  : 'From the customer once one is chosen')}
              </div>
            )}
            {repEditable && details.customerId && (
              <span className="bdp-hint">
                {details.customerId === PROSPECT_VALUE
                  ? 'A prospect has no account, so the rep is stored on the blend.'
                  : 'This customer has no rep, so the blend carries one.'}
              </span>
            )}
          </div>
        )}

        <div className="bdp-field">
          <label>Delivery address</label>
          {/* Read-only, and deliberately so. A typed address on a blend quote is
              a delivery instruction nobody checked against the account — the
              lorry goes where this says. It comes from the customer record, so
              correcting it means correcting the record, which fixes it
              everywhere rather than on this one document. */}
          <div className={`bdp-readonly${details.customerId ? '' : ' empty'}`}>
            {details.customerId
              ? (details.deliveryAddressLine || 'No delivery address on this account')
              : 'Pick a customer first'}
          </div>
          {details.customerId && !details.deliveryAddressLine && (
            <p className="bdp-hint">
              Add one on the contact record — it is the address the load is sent to.
            </p>
          )}
        </div>
      </div>

      <div className="bdp-section">
        <h4>Pricing</h4>

        <div className={`bdp-field${isMissing('priceColumnKeys') ? ' bdp-missing' : ''}`}>
          <label>Price basis <span className="bdp-required">required to export</span></label>
          {/* Tick list, not a dropdown: a quote can show more than one. */}
          {/* TickCheckbox, not a raw input — every checkbox in the system is
              the animated one, and a bare input here was the odd one out. */}
          <div className="bdp-checks">
            {priceColumns.map((c) => (
              <TickCheckbox
                key={c.key}
                id={`bdp-price-${c.key}`}
                checked={(details.priceColumnKeys || []).includes(c.key)}
                onChange={() => togglePriceColumn(c.key)}
                label={c.label}
              />
            ))}
          </div>
        </div>

        <div className="bdp-field">
          <label>Delivery band</label>
          {/* Drives the price, but never printed on a customer quote — the
              price basis takes that slot instead. */}
          <Dropdown
            options={[{ value: '', label: 'Raw material (ex works)' },
                      ...haulageBands.map((b) => ({ value: b.key, label: b.label }))]}
            value={details.haulageBandKey || ''}
            onChange={set('haulageBandKey')}
          />
        </div>

        <div className="bdp-field">
          <label>Contract period</label>
          <Dropdown options={periodOptions}
                    value={details.periodOfContract || ''}
                    onChange={set('periodOfContract')} />
        </div>
      </div>

      <div className="bdp-section">
        <h4>Order</h4>

        <div className="bdp-row">
          <div className="bdp-field">
            <label htmlFor="bdp-packaging">Delivery method</label>
            <Dropdown options={PACKAGING} value={details.packaging || ''} onChange={set('packaging')} />
          </div>
          <div className="bdp-field">
            <label htmlFor="bdp-terms">Delivery terms</label>
            <Dropdown options={DELIVERY_TERMS} value={details.deliveryTerms || ''} onChange={set('deliveryTerms')} />
          </div>
        </div>

        <div className="bdp-row">
          <div className="bdp-field">
            <label htmlFor="bdp-tonnes">Tonnes contracted</label>
            <Input id="bdp-tonnes" type="number" step="0.001" min="0"
                   value={details.tonnesContracted ?? ''}
                   onChange={(e) => set('tonnesContracted')(e.target.value)} />
          </div>
          {/* What it is SOLD at, which the calculator cannot work out — it
              knows the cost at each price column and haulage band, and the
              margin on top is somebody's decision.

              A ComboBox rather than a Dropdown: the computed prices are the
              useful starting points and are offered as such, but a quoted price
              is frequently a rounded or negotiated number that is on no list.
              Typing one is the normal case, not an escape hatch. */}
          <div className="bdp-field">
            <label htmlFor="bdp-sale-price">Sale price £/T</label>
            <ComboBox
              className="bdp-sale-price"
              dropUp
              options={salePriceOptions}
              value={details.salePrice ?? ''}
              /* Typing keeps the recorded basis: a rep who picks the Spot /
                 23T+ suggestion and then rounds it up still quoted on that
                 basis, and that is the case worth capturing. Clearing the
                 field clears it, because a blank price is based on nothing. */
              onChange={(v) => onChange(v === '' || v == null
                ? { ...details, salePrice: '', salePriceBasis: '', salePriceType: '' }
                : { ...details, salePrice: v })}
              onSelectOption={(opt) => onChange({
                ...details,
                salePrice: opt.value,
                salePriceBasis: opt.basisKey || '',
                salePriceType: opt.typeKey || ''
              })}
              placeholder="Select or type..."
              searchPlaceholder="Price…"
            />
            {salePriceOrigin && (
              <div className="bdp-price-origin">
                <span className="bdp-price-badge is-basis">{salePriceOrigin.basis}</span>
                <span className="bdp-price-badge is-type">{salePriceOrigin.type}</span>
                <button type="button" className="bdp-price-origin-clear"
                        title="Forget what this price was based on"
                        onClick={() => onChange({ ...details, salePriceBasis: '', salePriceType: '' })}>
                  <span className="material-symbols-rounded">close</span>
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="bdp-field">
          <label htmlFor="bdp-notes">Notes</label>
          <Textarea id="bdp-notes" rows={3}
                    value={details.notes || ''}
                    onChange={(e) => set('notes')(e.target.value)}
                    placeholder="Anything that should appear on the quote" />
        </div>
      </div>
    </aside>
      <ScrollMore scrollRef={scrollRef} remaining={scroll.remaining} />
    </div>
  );
}

export default BlendDetailsPanel;
